import { useEffect } from 'react'
import {
  Control,
  FieldErrors,
  FieldValues,
  FormState,
  Path,
} from 'react-hook-form'
import { View } from 'react-native'
import { useSharedValue } from 'react-native-reanimated'
import { StyleSheet } from 'react-native-unistyles'

import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldText from '@/components/form/FormFieldText'
import FormFieldOrganizationVaccines from '@/components/organization/cards/formFields/FormFieldOrganizationVaccines'
import AccordionItem from '@/components/shared/AccordionItem'
import Species from '@/enums/species'

interface Props<C extends FieldValues, _E extends FieldErrors> {
  control: Control<C, any>
  disabled: boolean
  formState: FormState<C>
  index: number
  open: boolean
  species?: Species
  vaccination?: any
}

const FosterVaccinationItem = <C extends FieldValues, E extends FieldErrors>({
  control,
  disabled,
  formState,
  index,
  open,
  species,
  vaccination,
}: Props<C, E>) => {
  const isExpanded = useSharedValue(open)

  useEffect(() => {
    isExpanded.value = open
  }, [isExpanded, open])

  return (
    <AccordionItem isExpanded={isExpanded} viewKey={`vaccination_${index}`}>
      <View style={styles.container}>
        <FormFieldOrganizationVaccines
          control={control}
          disabled={disabled}
          fieldName={`vaccinations.${index}.vaccineId` as Path<C>}
          formState={formState}
          label="Vaccine"
          species={species}
          style={styles.formField}
        />
        {!vaccination?.givenDate && (
          <FormFieldDate
            control={control}
            disabled={disabled}
            errors={formState.errors}
            fieldName={`vaccinations.${index}.dueDate` as Path<C>}
            label="Due On"
            required={false}
            style={styles.formField}
          />
        )}
        <FormFieldDate
          control={control}
          disabled={disabled}
          errors={formState.errors}
          fieldName={`vaccinations.${index}.givenDate` as Path<C>}
          label="Given On"
          required={false}
          style={styles.formField}
        />
        <FormFieldText
          control={control}
          disabled={disabled}
          errors={formState.errors}
          fieldName={`vaccinations.${index}.givenBy` as Path<C>}
          label="Given By"
          required={true}
          style={styles.formField}
        />
      </View>
    </AccordionItem>
  )
}

export default FosterVaccinationItem

const styles = StyleSheet.create((theme) => {
  return {
    container: {
      gap: theme.tokens.spacing[2],
      marginTop: theme.tokens.spacing[2],
      paddingHorizontal: theme.tokens.spacing[4],
      width: '100%',
    },
    formField: {
      flex: 1,
    },
  }
})
